import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Card } from '#app/components/ui/card.tsx'
import { type IconName } from '#app/components/ui/icon.tsx'
import { BrandLink } from './__brand-link.tsx'
import { IconTile } from './__icon-tile.tsx'
import { useReveal } from './__use-reveal.ts'

/**
 * The three flagship capabilities previewed on the home page — one per
 * `/features` group (design → build → ship). The keys and icons match the full
 * page's item keys (`features.groups.*`) so the teaser and `/features` stay one
 * story; the shortened copy lives under `features.teaser.items.<key>`.
 */
const TEASER_ITEMS: ReadonlyArray<{
	icon: IconName
	key: 'system' | 'frontend' | 'deploy'
}> = [
	{ icon: 'pencil-2', key: 'system' },
	{ icon: 'laptop', key: 'frontend' },
	{ icon: 'reset', key: 'deploy' },
]

/**
 * The home "capabilities" teaser (design-lane direction 1c) — a condensed 3-up
 * preview that replaces the old hand-rolled Services section and hands off to
 * the full `/features` route. A centered head (count pill + `h2` + lead) over
 * three `Card variant="interactive"` cards (top 3, one-liner body, no
 * checklist) and a closing brand link; every card and the link target
 * `/features`. Copy is migrated from the retired `landing.services.*` into
 * `features.teaser.*` so the teaser and the full page share one source. The
 * section keeps the `#services` anchor so the footer sitemap / ⌘K palette /
 * scrollspy targets still resolve. Reveal-on-scroll is progressive enhancement
 * via {@link useReveal}; the resting state renders server-side.
 */
export function FeatureTeaser() {
	const { t } = useTranslation('public')
	const ref = useRef<HTMLElement>(null)
	useReveal(ref)

	return (
		<section
			id="services"
			ref={ref}
			aria-labelledby="features-teaser-heading"
			className="container scroll-mt-20 py-24"
		>
			<div className="mx-auto max-w-2xl text-center">
				<p className="bg-brand-soft text-brand text-body-2xs inline-flex items-center rounded-full px-3 py-1 font-medium">
					{t('features.teaser.eyebrow')}
				</p>
				<h2
					id="features-teaser-heading"
					className="tracking-h2 mt-4 text-3xl font-semibold text-balance"
				>
					{t('features.teaser.heading')}
				</h2>
				<p className="text-muted-foreground mt-4 text-pretty">
					{t('features.teaser.lead')}
				</p>
			</div>

			<ul
				role="list"
				className="mx-auto mt-16 grid max-w-[960px] gap-6 md:grid-cols-3"
			>
				{TEASER_ITEMS.map((item, index) => (
					<li
						key={item.key}
						data-reveal
						style={{ transitionDelay: `${index * 90}ms` }}
					>
						<Link
							to="/features"
							className="focus-cosy group block h-full rounded-lg"
						>
							<Card variant="interactive" className="h-full gap-3 p-6">
								<IconTile name={item.icon} />
								<h3 className="tracking-h2 text-lg font-semibold">
									{t(`features.teaser.items.${item.key}.title`)}
								</h3>
								<p className="text-muted-foreground text-body-xs leading-relaxed text-pretty">
									{t(`features.teaser.items.${item.key}.body`)}
								</p>
							</Card>
						</Link>
					</li>
				))}
			</ul>

			<div className="mt-12 text-center">
				<BrandLink to="/features">{t('features.teaser.cta')}</BrandLink>
			</div>
		</section>
	)
}
